import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";

const TRANSITION = "transform 0.3s ease-in-out";

export default function useHeaderScroll() {
  const { pathname } = useLocation();

  const { isPC } = useCheckDevice();

  const lastScrollTop = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (isPC) return;

    const header = document.getElementById("header");

    if (!header) return;

    // 헤더 높이를 상수로 박으면 클래스가 바뀔 때마다 어긋난다
    const headerHeight = header.offsetHeight;

    // 1. [핵심] 페이지 이동(또는 마운트) 시 무조건 헤더가 보이도록 초기화
    header.style.top = "0px";
    header.style.transform = "translateY(0px)";
    header.style.transition = TRANSITION;

    // #filter는 데이터 로딩이 끝나야 붙거나(컬렉션·검색) 필터 패널 토글로 재마운트되므로
    // effect 실행 시점에 없다고 포기하지 않고 매번 다시 찾아서 헤더와 동기화한다
    let filter: HTMLElement | null = null;

    const syncFilter = () => {
      const next = document.getElementById("filter");

      if (next === filter) return filter;

      if (filter) {
        filter.style.top = "";
        filter.style.transform = "";
        filter.style.transition = "";
      }

      filter = next;

      if (filter) {
        filter.style.top = `${headerHeight}px`;
        // 새로 붙은 필터는 애니메이션 없이 현재 헤더 위치로 맞춘 뒤 transition을 건다
        filter.style.transform = header.style.transform;
        filter.style.transition = TRANSITION;
      }

      return filter;
    };

    const setTranslate = (value: string) => {
      header.style.transform = value;

      if (filter) filter.style.transform = value;
    };

    const updateScroll = () => {
      ticking.current = false;

      syncFilter();

      const currentScroll = window.scrollY || document.documentElement.scrollTop;

      if (currentScroll <= 0) {
        setTranslate("translateY(0px)");
        lastScrollTop.current = 0;
        return;
      }

      if (currentScroll > lastScrollTop.current) {
        // 필터가 스티키 위치(헤더 바로 아래)에 닿은 뒤부터 헤더를 감춘다
        const isFilterStuck = filter ? filter.getBoundingClientRect().top <= headerHeight + 1 : true;

        setTranslate(isFilterStuck ? `translateY(-${headerHeight}px)` : "translateY(0px)");
      } else {
        setTranslate("translateY(0px)");
      }

      lastScrollTop.current = currentScroll;
    };

    const requestUpdate = () => {
      if (ticking.current) return;

      ticking.current = true;
      window.requestAnimationFrame(updateScroll);
    };

    // 페이지 이동 후의 초기 스크롤 위치 저장 (보통 최상단이므로 0)
    lastScrollTop.current = window.scrollY || document.documentElement.scrollTop;
    syncFilter();

    // 스크롤 없이 #filter가 뒤늦게 붙는 경우(로딩 완료·패널 닫기)를 잡아준다
    const observer = new MutationObserver(() => {
      if (document.getElementById("filter") !== filter) requestUpdate();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", requestUpdate, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", requestUpdate);

      header.style.transition = "";
      header.style.transform = "";

      if (filter) {
        filter.style.top = "";
        filter.style.transition = "";
        filter.style.transform = "";
      }
    };
  }, [isPC, pathname]);
}
